import {
  type DemandLifecycle,
  variantLifecycle,
} from "~/components/demand-2/shared/demandLifecycle"
import type { EffectiveDemand } from "~/components/demand-2/demand/useEffectiveDemand"
import type { IVariantSummary } from "~/hooks/useVariantTableData"
import { comparableDraft, wholeChange } from "~/utils/demandPublishState"
import { getAlerts, getInfoValue } from "~/utils/forecast-alert-utils"
import { isInactiveStatus } from "~/utils/lifecycleUtils"

/**
 * A group row's leaves (`rowNode.allLeafChildren`). Structural rather than `IRowNode`, so tests can
 * pass plain objects.
 */
export type LeafNodes = readonly { data?: IVariantSummary }[]

type Effective = (v: IVariantSummary) => EffectiveDemand

/**
 * From the engine, not `Sku.isNpi`, which lags the forecast tables. Inactive lifecycle excluded:
 * the engine's NPI predicate has no lifecycle awareness, so lifecycle status wins, as in the
 * Lifecycle column.
 */
export const isDerivedNpi = (variant: IVariantSummary, effective: Effective) =>
  effective(variant).isNpi && !isInactiveStatus(variant.settings?.lifecycleStatus)

/**
 * Fashion's non-steady variants by right-now status (all but Ongoing and New). One walk feeds both
 * the Lifecycle group cell's total and its hover breakdown, so they agree. Cached by `leaves` (stable
 * per node until its data version changes): the aggFunc and cellRenderer each ask for it.
 */
const attentionCache = new WeakMap<
  LeafNodes,
  { asOf: number; counts: Map<DemandLifecycle, number> }
>()

export const lifecycleAttention = (leaves: LeafNodes, asOf: Date): Map<DemandLifecycle, number> => {
  const asOfMs = asOf.getTime()
  const cached = attentionCache.get(leaves)
  if (cached && cached.asOf === asOfMs) return cached.counts
  const counts = new Map<DemandLifecycle, number>()
  for (const n of leaves) {
    const d = n.data
    if (!d) continue
    const stage = variantLifecycle(d, asOf)
    if (stage !== "ongoing" && stage !== "live") counts.set(stage, (counts.get(stage) ?? 0) + 1)
  }
  attentionCache.set(leaves, { asOf: asOfMs, counts })
  return counts
}

export const lifecycleAttentionTotal = (counts: Map<DemandLifecycle, number>): number => {
  let total = 0
  for (const n of counts.values()) total += n
  return total
}

export const npiCount = (leaves: LeafNodes, isNew: (v: IVariantSummary) => boolean): number => {
  let npi = 0
  for (const n of leaves) if (n.data && isNew(n.data)) npi += 1
  return npi
}

// An info state suppresses the row's alerts (getAlerts returns [] whenever one is set), so a variant
// counts as one or the other, never both.
export const demandStatusCounts = (
  leaves: LeafNodes,
  threshold: number,
): { alerted: number; informed: number } => {
  let alerted = 0
  let informed = 0
  for (const n of leaves) {
    if (getInfoValue(n.data)) informed += 1
    else if ((getAlerts(n.data, threshold) ?? []).length > 0) alerted += 1
  }
  return { alerted, informed }
}

export const demandStatusSummary = ({
  alerted,
  informed,
}: {
  alerted: number
  informed: number
}): string =>
  [
    alerted > 0 ? `${alerted} alert${alerted === 1 ? "" : "s"}` : "",
    informed > 0 ? `${informed} info` : "",
  ]
    .filter(Boolean)
    .join(" · ")

type PublishSides = {
  effective: (v: IVariantSummary) => {
    totalDemand: number
    publishTail: number
    grain: "scoped"
  }
  published: Map<string, { published: number }> | null | undefined
}

/**
 * One variant's draft-minus-published delta from the grid context, in whole units as `wholeChange`
 * rounds it. Both sides are scoped: the published read takes the draft's scope.
 */
export const publishDriftFor = (variant: IVariantSummary, ctx: PublishSides): number =>
  wholeChange(
    comparableDraft(ctx.effective(variant)),
    ctx.published?.get(variant.variantId)?.published ?? 0,
  )

/** The variants' whole deltas summed, so a group moves by what its rows show. */
export const publishDriftOver = (variants: readonly IVariantSummary[], ctx: PublishSides): number =>
  variants.reduce((s, v) => s + publishDriftFor(v, ctx), 0)
